import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Spin, Alert, Tooltip } from 'antd';
import {
  getTerminals,
  getTerminalUsage,
  getServiceLines,
  type Terminal,
  type TerminalUsage,
  type ServiceLine,
} from '../services/clarusService';
import { ExportButton } from '../components/ExportButton';
import { priorityScope } from '../utils/subscriptionLabel';
import { useDisplaySettings } from '../theme/DisplaySettingsContext';
import { blurStyle } from '../utils/redact';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type StatusCategory = 'down' | 'overAllowance' | 'forecastOver' | 'healthy';

interface EnrichedTerminal extends Terminal {
  subscriptionDescription: string | null;
  usage: TerminalUsage | null;
  statusCategory: StatusCategory;
}

/** Terminals grouped under the same service-line number. */
interface SLNGroup {
  serviceLineNumber: string;
  /** Client / vessel / site name leading the row; null -> show the SLN instead. */
  name: string | null;
  subscriptionDescription: string | null;
  usage: TerminalUsage | null;
  terminals: EnrichedTerminal[];
  onlineCount: number;
  totalCount: number;
  /** Worst status across the line's terminals - drives the status chip and sort. */
  statusCategory: StatusCategory;
  /** True when the SLN's current usage exceeds its allowance. */
  lineIsOver: boolean;
  /** True when forecast exceeds allowance but not yet over. */
  lineIsForecastOver: boolean;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const CONCURRENCY_LIMIT = 10;
const CATEGORY_ORDER: StatusCategory[] = ['down', 'overAllowance', 'forecastOver', 'healthy'];

/** Status chip shown in the Status column; service-line rows only. */
const STATUS_CHIP: Record<
  StatusCategory,
  { label: string; border: string; bg: string; color: string }
> = {
  down: { label: 'Down', border: '#ffccc7', bg: '#fff2f0', color: '#cf1322' },
  overAllowance: { label: 'Over allowance', border: '#ffbb96', bg: '#fff2e8', color: '#ad4e00' },
  forecastOver: { label: 'Trending over', border: '#ffe58f', bg: '#fffbe6', color: '#8c6d00' },
  healthy: { label: 'Healthy', border: '#f0f0f0', bg: '#fafafa', color: 'rgba(0,0,0,.45)' },
};

/** Neutral left accent that brackets each service-line row - no status colour. */
const SLN_ACCENT = '#d9d9d9';

/** Selectable status-category filters shown as chips (multi-select, OR union). */
type StatusFilterKey = 'over' | 'trending';
const STATUS_FILTERS: {
  key: StatusFilterKey;
  label: string;
  bg: string;
  border: string;
  color: string;
}[] = [
  { key: 'over', label: 'Over allowance', bg: '#fff2e8', border: '#ad4e00', color: '#ad4e00' },
  { key: 'trending', label: 'Trending over', bg: '#fffbe6', border: '#8c6d00', color: '#8c6d00' },
];

/** Shared th style matching the design. */
const TH: React.CSSProperties = {
  padding: '12px 10px',
  fontWeight: 600,
  fontSize: 13,
  lineHeight: '20px',
  color: 'rgba(0,0,0,.88)',
  background: '#fafafa',
};

/** Shared td style matching the design. */
const TD: React.CSSProperties = {
  padding: '12px 10px',
  fontSize: 13,
  lineHeight: '20px',
  color: 'rgba(0,0,0,.88)',
  fontVariantNumeric: 'tabular-nums',
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

async function fetchWithConcurrency<T>(
  tasks: (() => Promise<T>)[],
  limit: number,
  onResult?: (result: T, index: number) => void,
  signal?: AbortSignal
): Promise<T[]> {
  const results: T[] = new Array(tasks.length);
  let nextIndex = 0;

  async function worker() {
    while (nextIndex < tasks.length) {
      if (signal?.aborted) return;
      const i = nextIndex++;
      try {
        const result = await tasks[i]();
        results[i] = result;
        onResult?.(result, i);
      } catch {
        // Individual failures are silently ignored — usage stays null
      }
    }
  }

  const workers = Array.from({ length: Math.min(limit, tasks.length) }, () => worker());
  await Promise.all(workers);
  return results;
}

function categorizeTerminal(t: Terminal, usage: TerminalUsage | null): StatusCategory {
  if (!t.isOnline) return 'down';
  if (usage) {
    if (usage.remainingAllowance !== null && usage.remainingAllowance <= 0) return 'overAllowance';
    if (usage.forecastToExceedAllowance) return 'forecastOver';
  }
  return 'healthy';
}

/** Comma-separated integer (e.g. 18204 → "18,204"). */
function fmtNum(n: number): string {
  return Math.round(n).toLocaleString('en-US');
}

/**
 * Format a data amount (received from the API in GB, decimal/metric base-1000)
 * into 3 significant digits with the most appropriate unit.
 *
 * Examples:  180 GB → { value: '180', unit: 'GB' }
 *           6000 GB → { value: '6.00', unit: 'TB' }
 *           1234 GB → { value: '1.23', unit: 'TB' }
 *           0.5  GB → { value: '500', unit: 'MB' }
 */
function fmtData(gb: number): { value: string; unit: string } {
  if (gb === 0) return { value: '0', unit: 'GB' };
  const abs = Math.abs(gb);
  const sign = gb < 0 ? '-' : '';

  // Tiers ordered largest → smallest (base 1000)
  const tiers: [string, number][] = [
    ['PB', 1e6],
    ['TB', 1e3],
    ['GB', 1],
    ['MB', 1e-3],
  ];

  for (let i = 0; i < tiers.length; i++) {
    const [unit, factor] = tiers[i];
    const v = abs / factor;
    if (v < 1 && i < tiers.length - 1) continue;

    // 3 significant digits — choose decimal places so we keep exactly 3
    let dec: number;
    if (v >= 99.95) dec = 0;
    else if (v >= 9.995) dec = 1;
    else dec = 2;

    const rounded = parseFloat(v.toFixed(dec));

    // If rounding pushed to 1000+, bump to the next larger unit
    if (rounded >= 1000 && i > 0) {
      const [largerUnit, largerFactor] = tiers[i - 1];
      const lv = abs / largerFactor;
      const ldec = lv >= 99.95 ? 0 : lv >= 9.995 ? 1 : 2;
      return { value: sign + lv.toFixed(ldec), unit: largerUnit };
    }

    // Add commas for values ≥ 1000 in the chosen unit (shouldn't happen, but safety)
    const formatted =
      rounded >= 1000 ? sign + Math.round(rounded).toLocaleString('en-US') : sign + v.toFixed(dec);

    return { value: formatted, unit };
  }

  return { value: sign + abs.toFixed(2), unit: 'MB' };
}

/** Shorthand: format a GB value as a single string like "6.00 TB". */
function fmtDataStr(gb: number): string {
  const { value, unit } = fmtData(gb);
  return `${value} ${unit}`;
}

/** Human-readable time-since string. */
function timeSince(iso: string | null): string {
  if (!iso) return '';
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} minute${mins === 1 ? '' : 's'} ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs === 1 ? '' : 's'} ago`;
  const days = Math.floor(hrs / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}

/** Offline duration from lastTelemetryAt. */
function offlineDuration(lastTelemetryAt: string | null): { text: string; seconds: number } | null {
  if (!lastTelemetryAt) return null;
  const sec = (Date.now() - new Date(lastTelemetryAt).getTime()) / 1000;
  if (sec < 0) return null;
  const d = Math.floor(sec / 86400);
  const h = Math.floor((sec % 86400) / 3600);
  const text = d > 0 ? `${d}d ${String(h).padStart(2, '0')}h` : h > 0 ? `${h}h` : '< 1h';
  return { text, seconds: sec };
}

/** Short date like "2 Sep" or "17 Sep". */
function fmtDate(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return `${d.getDate()} ${d.toLocaleDateString('en-US', { month: 'short' })}`;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const StarlinkTerminals2: React.FC = () => {
  const navigate = useNavigate();

  // --- Data state --------------------------------------------------------
  const [terminals, setTerminals] = useState<Terminal[]>([]);
  const [serviceLines, setServiceLines] = useState<ServiceLine[]>([]);
  const [usageMap, setUsageMap] = useState<Map<string, TerminalUsage>>(new Map());
  const [loading, setLoading] = useState(true);
  const [usageLoading, setUsageLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // --- UI state ----------------------------------------------------------
  const [search, setSearch] = useState('');
  const [statusFilters, setStatusFilters] = useState<Set<StatusFilterKey>>(new Set());
  const { redactNames } = useDisplaySettings();
  const [sortKey, setSortKey] = useState<'name' | 'status'>('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  /** Inline style that blurs identifying text when "hide names" is on. */
  const blur = blurStyle(redactNames);

  // --- Initial fetch: terminals + service lines in parallel ---------------
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    Promise.all([getTerminals(undefined, controller.signal), getServiceLines(controller.signal)])
      .then(([terminalData, slData]) => {
        setTerminals(terminalData);
        setServiceLines(slData.serviceLines);
        setError(null);
      })
      .catch(err => {
        if (!controller.signal.aborted) setError(err?.message || 'Failed to load data');
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, []);

  // --- Service-line lookup map -------------------------------------------
  const serviceLineMap = useMemo(() => {
    const map = new Map<string, ServiceLine>();
    for (const sl of serviceLines) map.set(sl.serviceLineNumber, sl);
    return map;
  }, [serviceLines]);

  // --- Background usage fetching -----------------------------------------
  useEffect(() => {
    if (loading || terminals.length === 0) return;
    const controller = new AbortController();
    setUsageLoading(true);

    const slNumbers = [
      ...new Set(
        terminals.map(t => t.serviceLineNumber).filter((sln): sln is string => sln !== null)
      ),
    ];
    if (slNumbers.length === 0) {
      setUsageLoading(false);
      return;
    }

    const tasks = slNumbers.map(sln => () => getTerminalUsage(sln, undefined, controller.signal));

    fetchWithConcurrency(
      tasks,
      CONCURRENCY_LIMIT,
      result => {
        if (controller.signal.aborted) return;
        setUsageMap(prev => {
          const next = new Map(prev);
          if (result?.usage) next.set(result.usage.serviceLineNumber, result.usage);
          return next;
        });
      },
      controller.signal
    ).finally(() => {
      if (!controller.signal.aborted) setUsageLoading(false);
    });

    return () => controller.abort();
  }, [loading, terminals]);

  // --- Enrich terminals ---------------------------------------------------
  const enrichedTerminals: EnrichedTerminal[] = useMemo(() => {
    // First pass: enrich with usage + individual status category.
    const initial: EnrichedTerminal[] = terminals.map(t => {
      const sl = t.serviceLineNumber ? serviceLineMap.get(t.serviceLineNumber) : null;
      const usage = t.serviceLineNumber ? (usageMap.get(t.serviceLineNumber) ?? null) : null;
      return {
        ...t,
        subscriptionDescription: sl?.subscription?.description ?? null,
        usage,
        statusCategory: categorizeTerminal(t, usage),
      };
    });

    // Second pass: for each SLN, find the worst category among all terminals
    // and assign it to every terminal on that SLN so they all stay together.
    const slnWorst = new Map<string, StatusCategory>();
    for (const t of initial) {
      if (!t.serviceLineNumber) continue;
      const prev = slnWorst.get(t.serviceLineNumber);
      if (!prev || CATEGORY_ORDER.indexOf(t.statusCategory) < CATEGORY_ORDER.indexOf(prev)) {
        slnWorst.set(t.serviceLineNumber, t.statusCategory);
      }
    }
    for (const t of initial) {
      if (!t.serviceLineNumber) continue;
      const worst = slnWorst.get(t.serviceLineNumber);
      if (worst) t.statusCategory = worst;
    }

    return initial;
  }, [terminals, serviceLineMap, usageMap]);

  // --- Filtering ----------------------------------------------------------
  const filtered = useMemo(() => {
    if (!search) return enrichedTerminals;
    const q = search.toLowerCase();
    return enrichedTerminals.filter(
      t =>
        (t.deviceId || '').toLowerCase().includes(q) ||
        (t.siteName || '').toLowerCase().includes(q) ||
        (t.clientName || '').toLowerCase().includes(q) ||
        (t.kitSerialNumber || '').toLowerCase().includes(q) ||
        (t.serviceLineNumber || '').toLowerCase().includes(q) ||
        (t.allocation?.assetName || '').toLowerCase().includes(q) ||
        (t.allocation?.friendlyName || '').toLowerCase().includes(q) ||
        (t.subscriptionDescription || '').toLowerCase().includes(q)
    );
  }, [enrichedTerminals, search]);

  // Flat list of service-line groups - no status banding. Each carries the worst
  // status across its terminals (for the chip + sort); terminals keep their own
  // online/offline state.
  const slnGroups = useMemo<SLNGroup[]>(() => {
    const nameOf = (terms: EnrichedTerminal[]): string | null =>
      terms
        .map(t => t.clientName || t.allocation?.assetName || t.siteName)
        .find((n): n is string => !!n) || null;
    const slnMap = new Map<string, EnrichedTerminal[]>();
    const noSLN: EnrichedTerminal[] = [];
    for (const t of filtered) {
      if (!t.serviceLineNumber) {
        noSLN.push(t);
        continue;
      }
      const arr = slnMap.get(t.serviceLineNumber);
      if (arr) arr.push(t);
      else slnMap.set(t.serviceLineNumber, [t]);
    }

    const groups: SLNGroup[] = [];
    for (const [sln, terms] of slnMap) {
      const usage = usageMap.get(sln) ?? null;
      const lineIsOver =
        usage != null && usage.remainingAllowance !== null && usage.remainingAllowance <= 0;
      const lineIsForecastOver = !lineIsOver && usage != null && !!usage.forecastToExceedAllowance;
      groups.push({
        serviceLineNumber: sln,
        name: nameOf(terms),
        subscriptionDescription: terms[0].subscriptionDescription,
        usage,
        terminals: terms,
        onlineCount: terms.filter(t => t.isOnline).length,
        totalCount: terms.length,
        statusCategory: lineIsOver
          ? 'overAllowance'
          : lineIsForecastOver
            ? 'forecastOver'
            : 'healthy',
        lineIsOver,
        lineIsForecastOver,
      });
    }
    // Terminals with no SLN each become their own solo group.
    for (const t of noSLN) {
      groups.push({
        serviceLineNumber: t.deviceId,
        name: nameOf([t]),
        subscriptionDescription: t.subscriptionDescription,
        usage: null,
        terminals: [t],
        onlineCount: t.isOnline ? 1 : 0,
        totalCount: 1,
        statusCategory: 'healthy',
        lineIsOver: false,
        lineIsForecastOver: false,
      });
    }
    return groups;
  }, [filtered, usageMap]);

  // Default order is client name (A->Z); the Terminal and Status headers toggle sort.
  const sortedGroups = useMemo<SLNGroup[]>(() => {
    const dir = sortDir === 'asc' ? 1 : -1;
    const byName = (a: SLNGroup, b: SLNGroup) =>
      (a.name || a.serviceLineNumber).localeCompare(b.name || b.serviceLineNumber);
    const matches = (g: SLNGroup): boolean =>
      statusFilters.size === 0 ||
      (statusFilters.has('over') && g.lineIsOver) ||
      (statusFilters.has('trending') && g.lineIsForecastOver);
    return slnGroups.filter(matches).sort((a, b) => {
      if (sortKey === 'status') {
        const d =
          CATEGORY_ORDER.indexOf(a.statusCategory) - CATEGORY_ORDER.indexOf(b.statusCategory);
        return d !== 0 ? d * dir : byName(a, b);
      }
      return byName(a, b) * dir;
    });
  }, [slnGroups, sortKey, sortDir, statusFilters]);

  const sortArrow = (key: 'name' | 'status') =>
    sortKey === key ? (sortDir === 'asc' ? ' \u2191' : ' \u2193') : '';
  const toggleSort = (key: 'name' | 'status') => {
    if (sortKey === key) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  // Chip counts, per service line (search-filtered). Categories can overlap.
  const statusCounts = useMemo(() => {
    let over = 0;
    let trending = 0;
    for (const g of slnGroups) {
      if (g.lineIsOver) over++;
      if (g.lineIsForecastOver) trending++;
    }
    return { all: slnGroups.length, over, trending };
  }, [slnGroups]);

  const toggleStatus = (key: StatusFilterKey) =>
    setStatusFilters(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const clearStatus = () => setStatusFilters(new Set());

  // --- Aggregate stats (from search-filtered list, before attention) ------
  const stats = useMemo(() => {
    // Terminal connectivity. isActive comes from Clarus user_terminal_active - a
    // service/account flag, false only when the terminal is deactivated in the
    // Starlink account (not when the hardware is powered off). So:
    //   online       = active + connected
    //   offline       = active but not reporting - powered off, obstructed or
    //                   faulted, which the data cannot tell apart
    //   deactivated   = service turned off in the account (intentional, dated)
    let onlineCount = 0;
    let offlineCount = 0;
    let deactivatedCount = 0;
    for (const t of filtered) {
      if (!t.isActive) deactivatedCount++;
      else if (t.isOnline) onlineCount++;
      else offlineCount++;
    }

    // Per-SLN stats
    const seenSLNs = new Set<string>();
    let totalUsage = 0;
    let totalAllowance = 0;
    let totalForecast = 0;
    let allForecastOverCount = 0;
    let totalForecastOverageGB = 0;

    for (const t of filtered) {
      if (!t.serviceLineNumber || seenSLNs.has(t.serviceLineNumber)) continue;
      seenSLNs.add(t.serviceLineNumber);
      const usage = usageMap.get(t.serviceLineNumber);
      if (usage) {
        totalUsage += usage.totalUsage ?? 0;
        totalAllowance += usage.subscriptionAllowance ?? 0;
        totalForecast += usage.forecastUsage ?? 0;
        if (
          usage.forecastToExceedAllowance &&
          usage.forecastUsage != null &&
          usage.subscriptionAllowance != null
        ) {
          allForecastOverCount++;
          totalForecastOverageGB += usage.forecastUsage - usage.subscriptionAllowance;
        }
      }
    }

    // Longest time an active terminal has been offline. Deactivated units are
    // excluded so intentional service-offs don't skew it.
    let longestOutageSec = 0;
    for (const t of filtered) {
      if (t.isActive && !t.isOnline && t.lastTelemetryAt) {
        const dur = offlineDuration(t.lastTelemetryAt);
        if (dur && dur.seconds > longestOutageSec) longestOutageSec = dur.seconds;
      }
    }

    let lastSync: string | null = null;
    for (const t of filtered) {
      if (t.lastSyncedAt && (!lastSync || t.lastSyncedAt > lastSync)) lastSync = t.lastSyncedAt;
    }

    return {
      onlineCount,
      offlineCount,
      deactivatedCount,
      totalTerminals: filtered.length,
      uniqueServiceLines: seenSLNs.size,
      totalUsage: Math.round(totalUsage),
      totalAllowance: Math.round(totalAllowance),
      totalForecast: Math.round(totalForecast),
      allForecastOverCount,
      totalForecastOverageGB: Math.round(totalForecastOverageGB),
      longestOutageSec,
      lastSyncLabel: lastSync ? `synced ${timeSince(lastSync)}` : null,
    };
  }, [filtered, usageMap]);

  // --- Navigation ---------------------------------------------------------
  const handleTerminalClick = useCallback(
    (deviceId: string) => navigate(`/starlink/${encodeURIComponent(deviceId)}`),
    [navigate]
  );

  // -----------------------------------------------------------------------
  // Render helpers
  // -----------------------------------------------------------------------

  /** Two-tone progress bar for the data card. */
  const renderProgressBar = () => {
    if (stats.totalAllowance <= 0) return null;
    const overForecast = stats.totalForecast > stats.totalAllowance;

    if (!overForecast) {
      // Forecast within allowance – green bar, track = 100 %
      const usedPct = (stats.totalUsage / stats.totalAllowance) * 100;
      const forecastPct = ((stats.totalForecast - stats.totalUsage) / stats.totalAllowance) * 100;
      return (
        <span style={{ display: 'block', position: 'relative', height: 8, marginTop: 9 }}>
          <span
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              width: '100%',
              height: '100%',
              background: '#f0f0f0',
              borderRadius: 99,
            }}
          />
          <span
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              width: `${usedPct}%`,
              height: '100%',
              background: '#52c41a',
              borderRadius: '99px 0 0 99px',
            }}
          />
          <span
            style={{
              position: 'absolute',
              left: `${usedPct}%`,
              top: 0,
              width: `${Math.max(0, forecastPct)}%`,
              height: '100%',
              background: '#d9f7be',
              borderRadius: '0 99px 99px 0',
            }}
          />
        </span>
      );
    }

    // Forecast exceeds allowance – yellow bar + hatched danger zone
    const trackPct = (stats.totalAllowance / stats.totalForecast) * 100;
    const usedPct = (stats.totalUsage / stats.totalForecast) * 100;
    const forecastRemPct = ((stats.totalForecast - stats.totalUsage) / stats.totalForecast) * 100;
    return (
      <span style={{ display: 'block', position: 'relative', height: 8, marginTop: 9 }}>
        <span
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            width: `${trackPct}%`,
            height: '100%',
            background: '#f0f0f0',
            borderRadius: 99,
          }}
        />
        <span
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            width: `${usedPct}%`,
            height: '100%',
            background: '#fadb14',
            borderRadius: '99px 0 0 99px',
          }}
        />
        <span
          style={{
            position: 'absolute',
            left: `${usedPct}%`,
            top: 0,
            width: `${forecastRemPct}%`,
            height: '100%',
            background: '#fff1b8',
            borderRadius: '0 99px 99px 0',
          }}
        />
        <span
          style={{
            position: 'absolute',
            left: `${trackPct}%`,
            top: 0,
            right: 0,
            height: '100%',
            background:
              'repeating-linear-gradient(135deg,rgba(207,19,34,.22) 0 2px,rgba(255,255,255,0) 2px 5px)',
          }}
        />
        <span
          style={{
            position: 'absolute',
            left: `${trackPct}%`,
            top: -2,
            width: 2,
            height: 12,
            background: '#cf1322',
          }}
        />
      </span>
    );
  };

  /** Data card supporting text. */
  const dataCardSupport = () => {
    const parts: React.ReactNode[] = [];
    if (stats.totalForecast > 0) parts.push(`Forecast ${fmtDataStr(stats.totalForecast)}`);
    if (stats.allForecastOverCount > 0) {
      parts.push(
        <React.Fragment key="over">
          {' '}
          &middot;{' '}
          <b style={{ fontWeight: 600, color: '#8c6d00' }}>
            {stats.allForecastOverCount} line{stats.allForecastOverCount === 1 ? '' : 's'} forecast
            over
          </b>{' '}
          by {fmtDataStr(stats.totalForecastOverageGB)}
        </React.Fragment>
      );
    }
    return parts;
  };

  /** Terminal overview sub-line: the longest an active terminal has been offline. */
  const terminalOverviewSupport = () => {
    if (stats.longestOutageSec <= 0) return '';
    const d = Math.floor(stats.longestOutageSec / 86400);
    const h = Math.floor((stats.longestOutageSec % 86400) / 3600);
    return `Longest offline ${d > 0 ? `${d}d ${String(h).padStart(2, '0')}h` : `${h}h`}`;
  };

  // -----------------------------------------------------------------------
  // Main render
  // -----------------------------------------------------------------------

  if (error && terminals.length === 0) {
    return (
      <div className="page-wrapper">
        <Alert type="error" showIcon message="Failed to load terminals" description={error} />
      </div>
    );
  }

  if (loading) {
    return (
      <div
        className="page-wrapper"
        style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}
      >
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="page-wrapper" style={{ background: '#f5f5f5', padding: '16px 24px 24px' }}>
      {/* ---- Page header ------------------------------------------------- */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 4,
        }}
      >
        <span
          style={{ fontWeight: 600, fontSize: 20, lineHeight: '28px', color: 'rgba(0,0,0,.88)' }}
        >
          Starlink
        </span>
        <span
          style={{
            width: 16,
            height: 16,
            border: '1px solid rgba(0,0,0,.25)',
            borderRadius: '50%',
            display: 'inline-block',
          }}
        />
      </div>

      {/* ---- Subtitle ---------------------------------------------------- */}
      <div style={{ fontSize: 14, lineHeight: '22px', color: 'rgba(0,0,0,.45)', marginBottom: 16 }}>
        {stats.totalTerminals} terminals &middot; {stats.uniqueServiceLines} service line
        {stats.uniqueServiceLines === 1 ? '' : 's'}
        {stats.lastSyncLabel ? ` · ${stats.lastSyncLabel}` : ''}
      </div>

      {/* ---- Two summary cards ------------------------------------------- */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.5fr', gap: 16, marginBottom: 16 }}>
        {/* Data card */}
        <div style={{ background: '#fff', borderRadius: 8, padding: '16px 20px' }}>
          <div style={{ fontSize: 13, lineHeight: '20px', color: 'rgba(0,0,0,.45)' }}>
            Data used
          </div>
          {usageLoading ? (
            <div
              style={{ fontSize: 24, lineHeight: '32px', color: 'rgba(0,0,0,.25)', marginTop: 2 }}
            >
              Loading usage&hellip;
            </div>
          ) : (
            <>
              <div
                style={{
                  fontSize: 24,
                  lineHeight: '32px',
                  color: 'rgba(0,0,0,.88)',
                  fontVariantNumeric: 'tabular-nums',
                  marginTop: 2,
                }}
              >
                {fmtDataStr(stats.totalUsage)}{' '}
                <span style={{ fontSize: 13, color: 'rgba(0,0,0,.45)', marginLeft: 3 }}>
                  of {fmtDataStr(stats.totalAllowance)}
                </span>
              </div>
              {(() => {
                const bar = renderProgressBar();
                if (!bar) return null;
                const over = stats.totalForecast > stats.totalAllowance;
                const tip =
                  `Used ${fmtDataStr(stats.totalUsage)} · ` +
                  `Forecast ${fmtDataStr(stats.totalForecast)} · ` +
                  `Allowance ${fmtDataStr(stats.totalAllowance)}` +
                  (over
                    ? ` · Over by ${fmtDataStr(stats.totalForecast - stats.totalAllowance)}`
                    : '');
                return (
                  <Tooltip title={tip} placement="top">
                    {bar}
                  </Tooltip>
                );
              })()}
              <div
                style={{ fontSize: 12, lineHeight: '18px', color: 'rgba(0,0,0,.45)', marginTop: 6 }}
              >
                {dataCardSupport()}
              </div>
            </>
          )}
        </div>

        {/* Terminal overview card - connectivity only, split by intent */}
        <div style={{ background: '#fff', borderRadius: 8, padding: '16px 20px' }}>
          <div style={{ fontSize: 13, lineHeight: '20px', color: 'rgba(0,0,0,.45)' }}>
            Terminal overview
          </div>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '6px 18px',
              marginTop: 2,
              alignItems: 'baseline',
            }}
          >
            <span
              style={{
                fontSize: 21,
                lineHeight: '28px',
                color: '#237804',
                fontVariantNumeric: 'tabular-nums',
                whiteSpace: 'nowrap',
              }}
            >
              {stats.onlineCount} <span style={{ fontSize: 14 }}>online</span>
            </span>
            <span
              style={{
                fontSize: 21,
                lineHeight: '28px',
                color: 'rgba(0,0,0,.45)',
                fontVariantNumeric: 'tabular-nums',
                whiteSpace: 'nowrap',
              }}
            >
              {stats.offlineCount} <span style={{ fontSize: 14 }}>offline</span>
            </span>
            <span
              style={{
                fontSize: 21,
                lineHeight: '28px',
                color: 'rgba(0,0,0,.45)',
                fontVariantNumeric: 'tabular-nums',
                whiteSpace: 'nowrap',
              }}
            >
              {stats.deactivatedCount} <span style={{ fontSize: 14 }}>deactivated</span>
            </span>
          </div>
          <div style={{ fontSize: 12, lineHeight: '18px', color: 'rgba(0,0,0,.45)', marginTop: 8 }}>
            {terminalOverviewSupport()}
          </div>
        </div>
      </div>

      {/* ---- Search + filter bar ----------------------------------------- */}
      <div
        className="no-print"
        style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}
      >
        {/* Custom search input with integrated blue button */}
        <div
          style={{
            flex: 'none',
            width: 280,
            display: 'flex',
            height: 32,
            border: `1px solid ${search ? '#1677ff' : '#d9d9d9'}`,
            borderRadius: 6,
            overflow: 'hidden',
            background: '#fff',
          }}
        >
          <input
            type="text"
            placeholder="Search name, serial, service line"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{
              flex: 1,
              padding: '5px 11px',
              fontSize: 14,
              lineHeight: '20px',
              border: 'none',
              outline: 'none',
              fontFamily: 'inherit',
              color: 'rgba(0,0,0,.88)',
              background: 'transparent',
            }}
          />
          <span
            style={{
              width: 38,
              background: '#1677ff',
              display: 'grid',
              placeItems: 'center',
              flexShrink: 0,
            }}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 16 16"
              fill="none"
              stroke="#fff"
              strokeWidth="1.6"
            >
              <circle cx="6.8" cy="6.8" r="4.6" />
              <path d="M10.2 10.2 14 14" />
            </svg>
          </span>
        </div>

        {/* All - clears the status filters */}
        <span
          onClick={clearStatus}
          style={{
            height: 32,
            padding: '0 15px',
            display: 'inline-flex',
            alignItems: 'center',
            borderRadius: 6,
            background: statusFilters.size === 0 ? '#1677ff' : '#fff',
            border: statusFilters.size === 0 ? '1px solid #1677ff' : '1px solid #d9d9d9',
            fontSize: 14,
            color: statusFilters.size === 0 ? '#fff' : 'rgba(0,0,0,.88)',
            cursor: 'pointer',
            flexShrink: 0,
            whiteSpace: 'nowrap',
            userSelect: 'none',
          }}
        >
          All &middot; {statusCounts.all}
        </span>

        {/* Status-category chips (multi-select, OR union) */}
        {STATUS_FILTERS.map(f => {
          const on = statusFilters.has(f.key);
          return (
            <span
              key={f.key}
              onClick={() => toggleStatus(f.key)}
              style={{
                height: 32,
                padding: '0 15px',
                display: 'inline-flex',
                alignItems: 'center',
                borderRadius: 6,
                background: on ? f.bg : '#fff',
                border: `1px solid ${on ? f.border : '#d9d9d9'}`,
                fontSize: 14,
                fontWeight: on ? 600 : 400,
                color: on ? f.color : 'rgba(0,0,0,.88)',
                cursor: 'pointer',
                flexShrink: 0,
                whiteSpace: 'nowrap',
                userSelect: 'none',
              }}
            >
              {f.label} &middot; {statusCounts[f.key]}
            </span>
          );
        })}

        {/* Export PDF button — shared component (filename + print CSS), like the SIM pages */}
        <span style={{ marginLeft: 'auto' }}>
          <ExportButton filename={() => `MIMO-Starlink-${new Date().toISOString().slice(0, 10)}`} />
        </span>
      </div>

      {/* ---- Terminal table ----------------------------------------------- */}
      {sortedGroups.length === 0 ? (
        <div
          style={{ textAlign: 'center', padding: '48px 0', fontSize: 14, color: 'rgba(0,0,0,.45)' }}
        >
          {search
            ? 'No terminals match your search'
            : statusFilters.size > 0
              ? 'No lines match the selected filters'
              : 'No terminals found'}
        </div>
      ) : (
        <div style={{ background: '#fff', borderRadius: 8, overflow: 'hidden' }}>
          {/* Column headers */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '2.05fr 1.2fr 1.95fr 1.6fr',
              borderBottom: '1px solid #f0f0f0',
            }}
          >
            <span
              style={{ ...TH, cursor: 'pointer', userSelect: 'none' }}
              onClick={() => toggleSort('name')}
            >
              Terminal{sortArrow('name')}
            </span>
            <span style={TH}>Service</span>
            <span style={TH}>Data this period</span>
            <span
              style={{ ...TH, cursor: 'pointer', userSelect: 'none' }}
              onClick={() => toggleSort('status')}
            >
              Status{sortArrow('status')}
            </span>
          </div>

          {/* Service-line rows - flat and sorted, no status banding */}
          {sortedGroups.map(group => {
            const used = group.usage?.totalUsage ?? null;
            const forecast = group.usage?.forecastUsage ?? null;
            const allowance = group.usage?.subscriptionAllowance ?? null;

            // KAN-191: client name (derived in slnGroups) leads; the service-line
            // number moves to the sub-line.
            const groupName = group.name;
            // Plan scope distilled from the billing SKU (Global / Local priority …).
            const scope = priorityScope(group.subscriptionDescription);

            // Pre-format data values
            const fmtUsed = fmtData(used ?? 0);
            const fmtForecastVal = forecast != null ? fmtData(forecast) : null;
            const fmtAllow = fmtData(allowance ?? 0);

            // Used colour
            const usedColor = group.lineIsOver ? '#ad4e00' : 'rgba(0,0,0,.65)';

            // Forecast colour / underline
            let forecastColor: string;
            let forecastLineColor: string;
            let showForecastGB = true;

            if (group.onlineCount === 0 || forecast == null) {
              forecastColor = 'rgba(0,0,0,.25)';
              forecastLineColor = '#f0f0f0';
              showForecastGB = false;
            } else if (group.lineIsOver) {
              forecastColor = '#ad4e00';
              forecastLineColor = '#ad4e00';
            } else if (group.lineIsForecastOver) {
              forecastColor = '#8c6d00';
              forecastLineColor = '#8c6d00';
            } else {
              forecastColor = '#237804';
              forecastLineColor = '#237804';
            }

            // SLN-level supporting text
            let slnSupportText = '';
            let slnSupportColor = 'rgba(0,0,0,.45)';
            if (group.lineIsOver && used != null && allowance != null) {
              slnSupportText = `Over by ${fmtDataStr(used - allowance)}`;
              slnSupportColor = '#ad4e00';
            } else if (group.lineIsForecastOver && group.usage?.allowanceConsumedDate) {
              slnSupportText = `Crosses on ${fmtDate(group.usage.allowanceConsumedDate)}`;
              slnSupportColor = '#8c6d00';
            }

            return (
              <React.Fragment key={group.serviceLineNumber}>
                {/* ---- SLN header row ---- */}
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '2.05fr 1.2fr 1.95fr 1.6fr',
                    borderBottom: '1px solid #f0f0f0',
                    borderLeft: `3px solid ${SLN_ACCENT}`,
                    background: '#f8f9fa',
                    alignItems: 'center',
                  }}
                >
                  {/* Col 1: client / vessel name, then service line · plan scope */}
                  <span style={{ padding: '10px 10px', fontSize: 13, lineHeight: '20px' }}>
                    <span style={{ fontWeight: 600, color: 'rgba(0,0,0,.65)', ...blur }}>
                      {groupName || group.serviceLineNumber}
                    </span>
                    <br />
                    <span style={{ fontSize: 12, color: 'rgba(0,0,0,.45)' }}>
                      {groupName && <span style={blur}>{group.serviceLineNumber}</span>}
                      {groupName && scope ? ' · ' : ''}
                      {scope}
                    </span>
                  </span>

                  {/* Col 2: online count */}
                  <span style={{ padding: '10px 10px', fontSize: 12, color: 'rgba(0,0,0,.45)' }}>
                    {group.onlineCount} of {group.totalCount} online
                  </span>

                  {/* Col 3: data triplet (Used → Forecast → Allowance) */}
                  <span style={{ padding: '10px 10px' }}>
                    {group.usage ? (
                      <>
                        <span style={{ display: 'flex', gap: 16, alignItems: 'flex-end' }}>
                          {/* Used */}
                          <span>
                            <span
                              style={{
                                display: 'block',
                                fontSize: 11,
                                lineHeight: '15px',
                                color: 'rgba(0,0,0,.45)',
                              }}
                            >
                              Used
                            </span>
                            <span
                              style={{
                                fontSize: 17,
                                lineHeight: '24px',
                                color: usedColor,
                                fontVariantNumeric: 'tabular-nums',
                              }}
                            >
                              {fmtUsed.value}
                              <span
                                style={{
                                  fontSize: 11,
                                  color: 'rgba(0,0,0,.45)',
                                  marginLeft: 2,
                                }}
                              >
                                {fmtUsed.unit}
                              </span>
                            </span>
                          </span>

                          {/* Arrow */}
                          <span
                            style={{
                              color: 'rgba(0,0,0,.25)',
                              fontSize: 13,
                              paddingBottom: 4,
                            }}
                          >
                            &rarr;
                          </span>

                          {/* Forecast */}
                          <span>
                            <span
                              style={{
                                display: 'block',
                                fontSize: 11,
                                lineHeight: '15px',
                                color: 'rgba(0,0,0,.45)',
                              }}
                            >
                              Forecast
                            </span>
                            <span
                              style={{
                                display: 'inline-block',
                                fontSize: 17,
                                lineHeight: '24px',
                                color: forecastColor,
                                fontVariantNumeric: 'tabular-nums',
                                borderBottom: `2px solid ${forecastLineColor}`,
                                paddingBottom: 1,
                              }}
                            >
                              {showForecastGB && fmtForecastVal ? (
                                <>
                                  {fmtForecastVal.value}
                                  <span
                                    style={{
                                      fontSize: 11,
                                      color: 'rgba(0,0,0,.45)',
                                      marginLeft: 2,
                                    }}
                                  >
                                    {fmtForecastVal.unit}
                                  </span>
                                </>
                              ) : (
                                'n/a'
                              )}
                            </span>
                          </span>

                          {/* Allowance */}
                          <span>
                            <span
                              style={{
                                display: 'block',
                                fontSize: 11,
                                lineHeight: '15px',
                                color: 'rgba(0,0,0,.45)',
                              }}
                            >
                              Allowance
                            </span>
                            <span
                              style={{
                                fontSize: 17,
                                lineHeight: '24px',
                                color: 'rgba(0,0,0,.45)',
                                fontVariantNumeric: 'tabular-nums',
                              }}
                            >
                              {fmtAllow.value}
                              <span
                                style={{
                                  fontSize: 11,
                                  color: 'rgba(0,0,0,.45)',
                                  marginLeft: 2,
                                }}
                              >
                                {fmtAllow.unit}
                              </span>
                            </span>
                          </span>
                        </span>

                        {slnSupportText && (
                          <span
                            style={{
                              display: 'block',
                              fontSize: 12,
                              color: slnSupportColor,
                              marginTop: 5,
                            }}
                          >
                            {slnSupportText}
                          </span>
                        )}
                      </>
                    ) : (
                      <span style={{ color: 'rgba(0,0,0,.25)' }}>&mdash;</span>
                    )}
                  </span>

                  {/* Col 4: status chip (service-line level) */}
                  <span style={{ padding: '10px 10px' }}>
                    {/* Exceptions only - a healthy line shows no chip (Data carries the numbers). */}
                    {group.statusCategory !== 'healthy'
                      ? (() => {
                          const chip = STATUS_CHIP[group.statusCategory];
                          return (
                            <span
                              style={{
                                display: 'inline-block',
                                padding: '0 7px',
                                borderRadius: 4,
                                fontSize: 12,
                                lineHeight: '20px',
                                border: `1px solid ${chip.border}`,
                                background: chip.bg,
                                color: chip.color,
                              }}
                            >
                              {chip.label}
                            </span>
                          );
                        })()
                      : null}
                  </span>
                </div>

                {/* ---- Terminal rows under this SLN ---- */}
                {group.terminals.map(t => {
                  const name =
                    t.allocation?.friendlyName ||
                    t.allocation?.assetName ||
                    t.siteName ||
                    t.clientName ||
                    null;
                  const displayName = name || t.kitSerialNumber || t.deviceId;

                  const offline = !t.isOnline ? offlineDuration(t.lastTelemetryAt) : null;

                  return (
                    <div
                      key={t.deviceId}
                      onClick={() => handleTerminalClick(t.deviceId)}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '2.05fr 1.2fr 1.95fr 1.6fr',
                        borderBottom: '1px solid #f0f0f0',
                        borderLeft: '3px solid transparent',
                        alignItems: 'center',
                        cursor: 'pointer',
                      }}
                      onMouseEnter={e => {
                        (e.currentTarget as HTMLElement).style.backgroundColor = '#fafafa';
                      }}
                      onMouseLeave={e => {
                        (e.currentTarget as HTMLElement).style.backgroundColor = '';
                      }}
                    >
                      {/* Col 1: terminal name + serial */}
                      <span
                        style={{
                          padding: '12px 10px 12px 20px',
                          fontSize: 13,
                          lineHeight: '20px',
                          color: 'rgba(0,0,0,.88)',
                        }}
                      >
                        <span style={{ fontWeight: 500, ...blur }}>{displayName}</span>
                        <br />
                        <span
                          style={{
                            fontSize: 12,
                            color: 'rgba(0,0,0,.45)',
                            fontVariantNumeric: 'tabular-nums',
                            ...blur,
                          }}
                        >
                          {t.kitSerialNumber || ''}
                        </span>
                      </span>

                      {/* Col 2: online/offline status */}
                      <span
                        style={{
                          padding: '12px 10px',
                          fontSize: 13,
                          lineHeight: '20px',
                          color: 'rgba(0,0,0,.88)',
                        }}
                      >
                        <i
                          style={{
                            display: 'inline-block',
                            width: 6,
                            height: 6,
                            borderRadius: '50%',
                            background: t.isOnline ? '#52c41a' : '#d9d9d9',
                            marginRight: 7,
                            verticalAlign: 1,
                            fontStyle: 'normal',
                          }}
                        />
                        {t.isOnline ? (
                          'Online'
                        ) : (
                          <>
                            Offline {offline && <b style={{ fontWeight: 600 }}>{offline.text}</b>}
                          </>
                        )}
                      </span>

                      {/* Col 3: empty (data shown on SLN header) */}
                      <span style={{ padding: '12px 10px' }} />

                      {/* Col 4: empty - a terminal's online/offline lives in the Service column */}
                      <span style={{ padding: '12px 10px' }} />
                    </div>
                  );
                })}
              </React.Fragment>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default StarlinkTerminals2;
